import { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { rem, Table } from "@mantine/core";

import { Loading } from "@/components/Loading";
import ScrollableTable from "@/components/ScrollableTable";
import SortableTh from "@/components/SortableTh";
import { ProfileContext } from "@/contexts/profile";
import { LabOrderResultsSortKeys } from "@/health-gorilla/types/labs";

interface LabOrderTableProps {
  isLoading: boolean;
  children: React.ReactNode;
  showClientColumn?: boolean;
  sortable?: boolean;
}

interface ProviderHeaderProps {
  showClientColumn: boolean;
  sortable: boolean;
}

export default function LabOrdersTable({
  isLoading,
  children,
  showClientColumn = false,
  sortable = false,
}: LabOrderTableProps) {
  const { isPatient } = useContext(ProfileContext);

  return (
    <ScrollableTable miw={rem(800)} scrollProps={{ mt: 0 }} data-testid="lab-orders-table">
      <Table.Thead bg="cream.3">
        {isPatient ? (
          <ClientHeader />
        ) : (
          <ProviderHeader sortable={sortable} showClientColumn={showClientColumn} />
        )}
      </Table.Thead>
      <Table.Tbody bg="white">
        {isLoading ? (
          <Table.Tr>
            <Table.Td colSpan={12}>
              <Loading height={96} />
            </Table.Td>
          </Table.Tr>
        ) : (
          children
        )}
      </Table.Tbody>
    </ScrollableTable>
  );
}

function ProviderHeader({ sortable, showClientColumn }: ProviderHeaderProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const colSpans = useMemo(() => {
    if (showClientColumn) {
      return {
        client: 3,
        labs: 2,
        requestor: 3,
        orderedDate: 1,
        receivedDate: 1,
        status: 1,
        action: 1,
      };
    }
    return {
      client: 0,
      labs: 3,
      requestor: 2,
      orderedDate: 2,
      receivedDate: 2,
      status: 1,
      action: 2,
    };
  }, [showClientColumn]);

  const handleSort = (sortKey: string | null) => {
    setSearchParams((prev) => {
      if (sortKey) {
        prev.set("sort", sortKey);
      } else {
        prev.delete("sort");
      }
      return prev;
    });
  };

  return (
    <Table.Tr>
      {showClientColumn && <Table.Th colSpan={colSpans.client}>Client</Table.Th>}
      <Table.Th colSpan={colSpans.labs}>Lab or Imaging Order</Table.Th>
      <Table.Th colSpan={colSpans.requestor}>Requestor</Table.Th>
      {sortable ? (
        <>
          <SortableTh
            onSort={handleSort}
            searchParams={searchParams}
            colSpan={colSpans.orderedDate}
            sortKey={LabOrderResultsSortKeys.OrderedDate}
          >
            Ordered
          </SortableTh>
          <SortableTh
            onSort={handleSort}
            searchParams={searchParams}
            colSpan={colSpans.receivedDate}
            sortKey={LabOrderResultsSortKeys.ReceivedDate}
          >
            Received
          </SortableTh>
        </>
      ) : (
        <>
          <Table.Th colSpan={colSpans.orderedDate}>Ordered</Table.Th>
          <Table.Th colSpan={colSpans.receivedDate}>Received</Table.Th>
        </>
      )}
      <Table.Th colSpan={colSpans.status}>Status</Table.Th>
      <Table.Th ta="right" colSpan={colSpans.action}>
        Action
      </Table.Th>
    </Table.Tr>
  );
}

function ClientHeader() {
  return (
    <Table.Tr>
      <Table.Th colSpan={4}>Lab or Imaging Order</Table.Th>
      {/* TODO[LABS]: Confirm with George if we should use Received here or keep Interpretation instead */}
      <Table.Th colSpan={2}>Date Received</Table.Th>
      <Table.Th colSpan={4}>Message</Table.Th>
      <Table.Th ta="right" colSpan={2}>
        Action
      </Table.Th>
    </Table.Tr>
  );
}
